import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Home, ArrowLeft, BarChart3 } from "lucide-react";
import ProfileCard, { UserProfileData } from "@/components/ProfileCard";

interface UserAttempt {
  id: string;
  completed: boolean;
  overall_score: number | null;
  started_at: string;
  completed_at: string | null;
  assessment_runs: {
    name: string;
  } | null;
  responses: any[];
}

const AdminUserDashboard = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user, isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState<UserAttempt[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoading && (!user || !isAdmin)) {
      navigate("/");
    }
  }, [user, isAdmin, isLoading, navigate]);

  useEffect(() => {
    if (user && isAdmin && userId) {
      fetchUserData();
    }
  }, [user, isAdmin, userId]);

  const fetchUserData = async () => {
    setLoading(true);

    // Fetch user profile
    const { data: profile } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (profile) {
      setUserProfile(profile as unknown as UserProfileData);
    }

    // Fetch user attempts
    const { data: attemptsData } = await supabase
      .from("attempts")
      .select(`
        *,
        assessment_runs:run_id(name),
        responses(*)
      `)
      .eq("user_id", userId)
      .order("started_at", { ascending: false });

    if (attemptsData) {
      setAttempts(attemptsData as UserAttempt[]);
    }

    setLoading(false);
  };

  const getScoreColor = (score: number | null) => {
    if (!score) return "text-muted-foreground";
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-blue-600";
    if (score >= 40) return "text-yellow-600";
    return "text-red-600";
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const completedAttempts = attempts.filter(a => a.completed);
  const avgScore = completedAttempts.length > 0
    ? completedAttempts.reduce((sum, a) => sum + (a.overall_score || 0), 0) / completedAttempts.length
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Bilingual Header */}
        <Card className="p-6 mb-6 bg-primary/5 border-primary/20">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-primary mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                User Dashboard - لوحة تحكم المستخدم
              </h1>
              <p className="text-lg text-foreground">
                {userProfile ? `${userProfile.first_name} ${userProfile.last_name}` : "Loading..."}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate(`/admin/user-analytics/${userId}`)}
                variant="outline"
                className="gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                Analytics - التحليلات
              </Button>
              <Button
                onClick={() => navigate("/admin/participants")}
                variant="outline"
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back - رجوع
              </Button>
              <Button
                onClick={() => navigate("/")}
                variant="default"
                className="gap-2"
              >
                <Home className="h-4 w-4" />
                Home - الرئيسية
              </Button>
            </div>
          </div>
        </Card>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="overview">Overview & History - نظرة عامة والسجل</TabsTrigger>
            <TabsTrigger value="profile">Full Profile - الملف الشخصي الكامل</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {/* Statistics Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                  Total Attempts - إجمالي المحاولات
                </h3>
                <p className="text-3xl font-bold text-primary">{attempts.length}</p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                  Completed - المكتمل
                </h3>
                <p className="text-3xl font-bold text-success">{completedAttempts.length}</p>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-sm font-medium text-muted-foreground mb-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                  Average Score - المعدل
                </h3>
                <p className={`text-3xl font-bold ${getScoreColor(avgScore)}`}>
                  {avgScore.toFixed(1)}%
                </p>
              </Card>
            </div>

            {/* Assessment History */}
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-6 text-primary" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                Assessment History - سجل التقييمات
              </h2>

              {attempts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-lg text-muted-foreground mb-4" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                    No assessment attempts yet - لا توجد محاولات تقييم حتى الآن
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Assessment Run - جلسة التقييم</TableHead>
                        <TableHead>Status - الحالة</TableHead>
                        <TableHead>Score - النتيجة</TableHead>
                        <TableHead>Started - تاريخ البدء</TableHead>
                        <TableHead>Completed - تاريخ الانتهاء</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attempts.map((attempt) => (
                        <TableRow key={attempt.id}>
                          <TableCell className="font-medium">
                            {attempt.assessment_runs?.name || "N/A"}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`px-2 py-1 rounded-full text-xs ${attempt.completed
                                  ? "bg-success/20 text-success"
                                  : "bg-muted text-muted-foreground"
                                }`}
                            >
                              {attempt.completed ? "Completed - مكتمل" : "In Progress - قيد التنفيذ"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className={`font-bold ${getScoreColor(attempt.overall_score)}`}>
                              {attempt.overall_score
                                ? `${attempt.overall_score.toFixed(1)}%`
                                : "N/A"}
                            </span>
                          </TableCell>
                          <TableCell>
                            {new Date(attempt.started_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {attempt.completed_at
                              ? new Date(attempt.completed_at).toLocaleDateString()
                              : "N/A"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="profile">
            {userProfile ? (
              <ProfileCard profile={userProfile} />
            ) : (
              <div className="text-center py-12">
                <p>Profile not found.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminUserDashboard;
